import React, { useState, useRef, useEffect } from "react";
import "./ChatMessagesContainer.css";
import { useUser } from "../../../context/UserContext";
import { getCurrentTime } from "../../Helper/currDataTime";
import {
  cleanHtmlMarkdownBlock,
  extractContentAfterMarker,
  extractFirstTitle,
  splitByMarker,
} from "../uiUtils/ChatboxWrapper/ChatbotCompStream";
import { getPreviousChatV1 } from "../../../services/api";
import SalesPlayCard from "./SalesPlayCard";
import UniversalShadowWrapperV2 from "../genericComponents/uiElements/UniversalShadowWrapperV2";
import { extractTextContentFormatted } from "../../../utilities/uiUtils";
import { useBanner } from "../../../context/BannerContext";
import { CopyLineDuotone } from "../../../svgs/CopyLineDuotone";
import {
  addStreamListener,
  initStreamSW,
  resumeStream,
  startStream,
  stopStream,
} from "../../../streaming/streamSwClient";
import { saveChatHistory } from "../../../services/iqApiV2";
import ThinkingCollapse from "./ThinkingCollapse";
import MnMLoader from "../../HtmlElements/MnMLoader";


const artifactMarkers = [
  "*~EOF_TOC~**~ppt~*",
  "*~EOF_TOC~**~pdf~*",
  // "*~EOF_TOC~**~html~*",
];


function formatFirstName(name) {
  if (!name || typeof name !== "string") return "there";
  const trimmed = name.trim();
  if (!trimmed) return "there";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function ChatMessagesContainer({
  setIntentData,
  sessionId,
  setChatHeadings,
  setSelectedIndex,
}) {
  const streamingRef = useRef("");
  const streamingDomRef = useRef(null);
  const thinkingDomRef = useRef(null);
const markerReachedRef = useRef(false);

  const showBanner = useBanner();
  const [chat, setChat] = useState([]);
  const { user } = useUser();
  const inputRef = useRef(null);
  const bottomRef = useRef(null); 
  const chatContainerRef = useRef(null); 
  const [isAtBottom, setIsAtBottom] = useState(true); 


  /* -------- RESUME STREAM ON SESSION CHANGE -------- */
  useEffect(() => {
    resumeStream(sessionId);
  }, [sessionId]);

  /* -------- INIT SERVICE WORKER + STREAM LISTENER -------- */
  useEffect(() => {
    let removeListener;

    initStreamSW()
      .then(() => {
        setIntentData({intent:null,data:null})
        removeListener = addStreamListener((data) => {
          const { type, sessionId: swSessionId, full, json,delta } = data;
          if (swSessionId !== sessionId) return;

          /* ------ STREAMING DELTA ------ */
          if (type === "STREAM_DELTA") {
            const marker = "*~EOF_TOC~*";

            // If marker is NOT reached yet → keep logging
            if (!markerReachedRef.current) {
              // Check if marker appears in this delta OR full text stream
              if (delta && delta?.includes(marker)) {
                console.log(" Marker encountered → stopping logs");
                markerReachedRef.current = true;

                // Log only the content BEFORE the marker
                const beforeMarker = delta.split(marker)[0];
                if (beforeMarker.trim() !== "") {
                  console.log("STREAM_DELTA (until marker):", beforeMarker);
                }
              }else{
                //  console.log("STREAM_DELTA : ", delta);
                   if (thinkingDomRef.current) {
              thinkingDomRef.current.innerHTML = delta;
            }
              } 
            }

            // --- EXISTING LOGIC ---
            const { before, after } = splitByMarker(full);

            // if (thinkingDomRef.current) {
            //   thinkingDomRef.current.innerHTML = cleanHtmlMarkdownBlock(before);
            // }

            if (streamingDomRef.current) {
              streamingDomRef.current.innerHTML = cleanHtmlMarkdownBlock(after);
            }

            streamingRef.current = after;
          }


          /* ------ STREAM COMPLETE ------ */
          if (type === "STREAM_COMPLETE") {
            const { before, after } = splitByMarker(full);
            if (thinkingDomRef.current) {
              thinkingDomRef.current.innerHTML = cleanHtmlMarkdownBlock(before);
            }
             const hasArtifact = artifactMarkers.some((m) => full?.includes(m));

            setChat((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                content:cleanHtmlMarkdownBlock(after),
                thought: before,
                streaming: false,
              };
              return updated;
            });

             if (hasArtifact) {
    setChat((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...updated[updated.length - 1],
        openArtifact: true,
        intent: "RENDER_GENERIC",
      };
      return updated;
    });
  }
          }

          /* ------ STREAM JSON ARTIFACT ------ */
          if (type === "STREAM_JSON") {
            const message = json?.message;
            const intent = json?.intent;

            if (intent === "contacts") {
              setIntentData({
                intent: "RENDER_CONTACT",
                data: json.data?.people,
              });
            } else if (intent === "opportunities") {
              setIntentData({
                intent: "RENDER_OPPS",
                data: json.data,
              });
            }

            setChat((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                content: message,
                text: intent === "contacts" ? json.data?.people : json.data,
                streaming: false,
                openArtifact: true,
                intent,
              };
              return updated;
            });
          }

          /* ------ STREAM ERROR ------ */
          if (type === "STREAM_ERROR") {
            console.error("Stream error:", data.error);
          }
        });
      })
      .catch(console.error);

    return () => {
      stopStream(sessionId).catch(() => {});
      if (removeListener) removeListener();
    };
  }, [sessionId, chat.length, setIntentData]);

  /* -------- LOAD PREVIOUS CHAT -------- */
useEffect(() => {
  setChat([]);
  setIntentData({ intent: null });

  async function loadPrev() {
    const resp = await getPreviousChatV1(sessionId);
    const raw = resp?.data?.data || [];

    const processed = raw
      .map((chatItem) => {
        const q = chatItem?.question;

        // Normalize into a safe string for markers and UI
        const questionStr =
          typeof q === "string"
            ? q
            : q?.message || q?.text || "";

        // Apply split safely
        const { before, after } = splitByMarker(questionStr);

        // Detect artifacts on safe string
        const hasArtifact = artifactMarkers.some((m) =>
          questionStr?.includes(m)
        );

        // Extract answer
        const answer = chatItem?.answer;

        // Extract modified date
        const modifiedDate = chatItem?.modified_date?.["$date"] || "";

        // Extract intent only if question is object
        const intent = typeof q === "object" ? q?.intent : null;

        // Extract intent text
        const intentText =
          intent === "contacts"
            ? q?.data?.people
            : q?.data;

        // Extract message (fallback)
        const intentTextMsg =
          typeof q === "object" ? q?.message : after;

        return {
          role: answer === "user_input" ? "user" : "bot",
          text: intentText || questionStr,
          content: intentTextMsg || questionStr,
          time: modifiedDate,
          openArtifact: Boolean(intentText) || hasArtifact,
          intent: hasArtifact ? "RENDER_GENERIC" : intent,
          thought: hasArtifact ? before:null,
        };
      });

    // If last was user → preload bot streaming placeholder
    if (
      processed?.length > 0 &&
      raw?.[raw.length - 1]?.answer === "user_input"
    ) {
      processed.push({
        role: "bot",
        text: "",
        content: "",
        streaming: true,
      });
    }

    setChat(processed);
  }

  loadPrev();
}, [sessionId]);
  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;

    const handleScroll = () => {
      const threshold = 60; // px from bottom
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      setIsAtBottom(atBottom);
    };

    el.addEventListener("scroll", handleScroll);
    handleScroll(); // run once to set initial state
    return () => el.removeEventListener("scroll", handleScroll);
  }, [chat]);
  useEffect(() => {
    if (isAtBottom) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat, isAtBottom]);


  /* -------- SEND USER MESSAGE -------- */
  const handleSend = async () => {
    const currentInput = inputRef.current.value;
    if (!currentInput.trim()) return;

    if (chat.length === 0) {
      setChatHeadings((prev) => [
        { _id: sessionId, heading: currentInput },
        ...prev,
      ]);
      setSelectedIndex(0);
    }
    markerReachedRef.current = false;
    const userMsg = {
      role: "user",
      text: currentInput,
      content:currentInput,
      time: getCurrentTime(),
    };
    const botInit = {
      role: "bot",
      text: "",
      time: getCurrentTime(),
      streaming: true,
    };

    inputRef.current.value = "";
    setChat((prev) => [...prev, userMsg, botInit]);

    try {
      await saveChatHistory(
        currentInput,
        "user_input",
        sessionId,
        user?.company_name,
        ""
      );

      function safeGetContent(msg) {
        try {
          if (!msg || !msg.content) return "";
          return handleCopy(msg.content, true) || "";
        } catch (e) {
          console.error("handleCopy error:", e);
          return "";
        }
      }

      const safeInput = typeof currentInput === "string" ? currentInput : (currentInput || "");

      const hasEnoughHistory = Array.isArray(chat) && chat.length >= 2;

      const prevUserMsg = hasEnoughHistory ? chat[chat.length - 2] : null;
      const prevSystemMsg = hasEnoughHistory ? chat[chat.length - 1] : null;

      const inputWithContext = hasEnoughHistory
        ? `${safeInput}\n\nHistory:\n\nUSER PROMPT: ${safeGetContent(prevUserMsg)}\n\nSYSTEM PROMPT: ${safeGetContent(prevSystemMsg)}`
        : safeInput;



      await startStream(sessionId, process.env.REACT_APP_STREAM_URL, {
        userInput: inputWithContext,
        clientName: user?.company_name,
        accountName: "",
        saveToken: localStorage.getItem("access_token"),
        userId:user?.id
      });

    } catch (err) {
      console.error("Stream start failed:", err);
    }
  };

  /* -------- COPY BUTTON -------- */
  const handleCopy = (htmlString, onlyParse = false) => {
    try {
      const temp = document.createElement("div");
      temp.innerHTML = htmlString;
      const txt = extractTextContentFormatted(temp);
      if (!onlyParse) {
        navigator.clipboard.writeText(txt);
        showBanner({
          message: "Content Copied",
          title: "Alert",
          type: "success",
          duration: 1000,
        });
      }else{
        return txt
      }
    } catch (e) {
      console.error("Copy failed:", e);
    }
  };


  return (
    <div className="cmc-page-wrapper">
      <div className={`cmc-greeting-text ${chat.length > 0 ? "cmc-hide" : ""}`}>
        Hey {formatFirstName(user?.firstname)}, what's your next move today?
      </div>

      <div className="cmc-messages-container" ref={chatContainerRef}>
        <div className="cmc-messages-inner">
          {chat?.map((msg, i) => {
            const isUser = msg.role === "user";

            return (
              <div
                key={i}
                className={`cmc-bubble ${
                  isUser ? "cmc-user" : "cmc-bot"
                }`}
              >
               
                {isUser ? (
                  msg.text
                ) : (
                 <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      gap: "1rem",
                      width: "100%"
                    }}
                  >

                   {msg.role === "bot" &&  <><MnMLoader isComplete={!msg.streaming}/></>}
                    <div style={{ position: "relative",flex: 1, minWidth: 0  }}>
                      {/* AFTER STREAM ENDS: Show thinking in collapse */}
                      
                      {msg.thought && !msg.streaming && (
                        <ThinkingCollapse thinkingHTML={msg.thought} />
                      )}

                      {/* DURING STREAM: LIVE thinking + answer */}
                      {msg.streaming && msg.role === "bot" ? (
                        <>
                          {/* 🔹 LIVE THINKING BLOCK */}
                    <div className="thinking-process-wrapper">

                     
                     
                       <UniversalShadowWrapperV2
                          htmlContent={""}
                          contentRef={(el) => {
                            if (!el) return;

                            // Detect empty wrapper
                            const isEmpty = el.innerHTML.trim() === "";

                            if (i === chat.length - 1) {
                              thinkingDomRef.current = el;

                              if (isEmpty) {
                                // 🟦 Show thinking indicator
                                el.innerHTML = `<div class="thinking-chip">Thinking...</div>`;
                              }
                            }
                          }}
                        />

                 
                    </div>


                          {/* 🔹 LIVE ANSWER BLOCK */}
                       <div
                           className="live-streaming-html">
                          <UniversalShadowWrapperV2
                                htmlContent=""
                                contentRef={(el) => {
                                  if (i === chat.length - 1) streamingDomRef.current = el;
                                }}
                              /></div>
                        </>
                      ) : msg.openArtifact ? (
                        <span>
                          <SalesPlayCard
                            title={extractFirstTitle(msg.content)}
                            time={msg.time}
                            onClickArtifact={() => {
                              if (msg.intent === "contacts") {
                                setIntentData({
                                  intent: "RENDER_CONTACT",
                                  data: msg.text,
                                });
                              } else if (msg.intent === "opportunities") {
                                setIntentData({
                                  intent: "RENDER_OPPS",
                                  data: msg.text,
                                });
                              }else if(msg.intent === "RENDER_GENERIC"){
                                setIntentData({
                                  intent: "RENDER_GENERIC",
                                  data: msg.content,
                                });
                              }
                            }}
                          />
                        </span>
                      ) : (
                        <>
                          <UniversalShadowWrapperV2
                            htmlContent={cleanHtmlMarkdownBlock(msg.content)}
                          />

                          <button
                            className="copy-btn"
                            onClick={() =>
                              handleCopy(
                                cleanHtmlMarkdownBlock(msg.content)
                              )
                            }
                          >
                            <CopyLineDuotone width="1rem" height="1rem" />
                          </button>
                        </>
                      )}
                    </div>

        
                  </div>
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>
      {/* Scroll-to-bottom button */}
      {!isAtBottom && (
        <div
          className="cmc-scroll-to-bottom-btn"
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
        >
          ↓
        </div>
      )}

      {/* INPUT */}
      <div
        className={`cmc-input-row ${
          chat.length > 0 ? "cmc-to-bottom" : "cmc-center"
        }`}
      >
        <input
          ref={inputRef}
          className="cmc-input"
          placeholder="Ask me anything"
          // disabled={disableUserInput}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />
        <button className="cmc-send-btn" 
        // disabled={disableUserInput}
        
        onClick={handleSend}>
          <span>↑</span>
        </button>
      </div>
    </div>
  );
}

export default ChatMessagesContainer;
